// Stellar Onboarding landing page.
//
// Tone rules from PC: plain English, no slang, no metaphors, no GPT
// sing-song. This page tells a founder what the workspace is and what
// will happen if they spend 12 minutes setting it up. Nothing more.
import { Link } from "wouter";
import { useCohort, fp } from "@/lib/founder-cohort";
import {
  ArrowRight, Sparkles, FolderCheck, Gauge, MessagesSquare, Eye,
  Target, ShieldCheck, Users, FileEdit,
} from "lucide-react";
import { SIA } from "./sia-voice";

export default function StellarOnboarding() {
  const { slug } = useCohort();
  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Hero */}
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5C00]/10 border border-[#FF5C00]/30 mb-5">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FF5C00]">
            Onboarding · about 12 minutes
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-4 max-w-3xl">
          Welcome to Favcy Select.
        </h1>
        <p className="text-base md:text-lg text-white/75 leading-relaxed max-w-2xl mb-7">
          This is the workspace you will use to raise your next round of funding.
          Setup takes about 12 minutes. After that, an Advisor will be assigned
          to you, Meera will produce a first read of your company, and Sia will
          start working in the background.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            href={fp(slug, "onboarding/begin")}
            data-testid="link-begin-onboarding"
            className="inline-flex items-center gap-2 bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white text-base font-semibold rounded-md px-5 py-3"
          >
            Start onboarding <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            href={fp(slug)}
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white px-4 py-3"
          >
            Not now
          </Link>
        </div>
      </div>

      {/* Sia intro */}
      <div className="mb-10 bg-gradient-to-br from-[#FF5C00]/10 via-[#FF5C00]/5 to-transparent border border-[#FF5C00]/30 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF5C00]/20 border border-[#FF5C00]/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#FF5C00]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-sm font-bold">{SIA.displayName}</span>
              <span className="text-[11px] text-white/60">{SIA.role}</span>
            </div>
            <p className="text-sm text-white/85 leading-relaxed mb-2">
              {SIA.greetings.awaitingOnboarding}
            </p>
          </div>
        </div>
      </div>

      {/* What is inside */}
      <SectionHeading title="What is inside" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
        <Feature
          icon={Sparkles}
          title="Sia"
          body="An AI that drafts your weekly update, finds proof from your files, and shows you what investors are asking. Sia drafts. You approve."
        />
        <Feature
          icon={MessagesSquare}
          title="Your Advisor"
          body="One person at Favcy who knows your business. Same person every week. Every request from them comes with the reason."
        />
        <Feature
          icon={Gauge}
          title="Investor Readiness score"
          body="A score broken into parts. Each missing part tells you which file or document closes it and how much your score will move."
        />
        <Feature
          icon={FolderCheck}
          title="Proof Engine"
          body="Upload a file or paste a Drive link. Each upload shows when it was added, who added it, and how it changed your score."
        />
        <Feature
          icon={ShieldCheck}
          title="Review Room"
          body="Every claim about your company that an advisor, Meera, or admin makes goes here first. You accept it, correct it, or reject it before it goes anywhere."
        />
        <Feature
          icon={FileEdit}
          title="Weekly Update"
          body="Sia asks you 5 to 7 short questions. She turns the answers into a draft update. You edit. Your Advisor reviews. Admin approves. The update goes to your investor page."
        />
        <Feature
          icon={Eye}
          title="Investor Page Preview"
          body="See exactly what an investor sees on your page. A button next to each section sends a change request to your Advisor."
        />
        <Feature
          icon={Target}
          title="Market Access Missions"
          body="Customer interviews, enterprise pilots, mentor sessions, PR work, and investor objection practice. Run as short structured sprints."
        />
        <Feature
          icon={Users}
          title="Founder Network"
          body="Five founder introductions per quarter, picked by us, at your stage and in a similar business. Plus a list of senior mentors you can choose to use."
        />
      </div>

      {/* How it works */}
      <SectionHeading title="How onboarding works" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-12">
        <Step
          n={1}
          title="Connect"
          body="Link your Google Drive folder or upload your latest pitch deck. We do not read your inbox. We do not pull anything you did not share."
        />
        <Step
          n={2}
          title="Snapshot"
          body="Meera reads what you shared and writes a first read of your company: stage, business model, traction, gaps."
        />
        <Step
          n={3}
          title="Correct"
          body="You see Meera's draft in Review Room. You correct anything that is wrong. What you accept becomes the start of your investor profile."
        />
      </div>

      {/* What changes for you */}
      <SectionHeading title="What changes for you" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
        <Plain
          title="You stop writing investor updates from scratch."
          body="Sia drafts the update from the week's actual data. You edit. Your Advisor sharpens. Admin approves. The investor sees a real update, not a forwarded email."
        />
        <Plain
          title="You stop guessing what investors want next."
          body="The Investor Demand Brief shows the questions and objections investors have raised, with the answer your last update already gave when one exists."
        />
        <Plain
          title="You stop chasing proof at the wrong time."
          body="Proof requests come from your Advisor with the reason. Sia helps you find what is already in your Drive before you write anything new."
        />
        <Plain
          title="You stop being one of fifty in a portfolio."
          body="You have one Advisor and one persistent thread. Your stage decides which nudges you get. Nothing is generic."
        />
      </div>

      {/* What you can count on */}
      <div className="bg-black/40 border border-white/[0.06] rounded-xl p-6 mb-10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-4">
          What you can count on
        </div>
        <div className="space-y-3 text-sm text-white/80">
          <p>1. Sia drafts. You approve. Nothing reaches an investor without your sign-off.</p>
          <p>2. Every claim about your company goes to Review Room first. You can accept it, correct it, or reject it.</p>
          <p>3. One Advisor. The same person every week. They know your business.</p>
        </div>
      </div>

      {/* Final CTA */}
      <div className="text-center py-6">
        <Link
          href={fp(slug, "onboarding/begin")}
          data-testid="link-begin-onboarding-bottom"
          className="inline-flex items-center gap-2 bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white text-base font-semibold rounded-md px-6 py-3"
        >
          Start onboarding <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-4">{title}</h2>
  );
}

function Feature({ icon: Icon, title, body }: { icon: any; title: string; body: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
      <div className="w-8 h-8 rounded-md bg-[#FF5C00]/15 border border-[#FF5C00]/30 flex items-center justify-center mb-2.5">
        <Icon className="w-4 h-4 text-[#FF5C00]" />
      </div>
      <h3 className="text-sm font-bold mb-1">{title}</h3>
      <p className="text-[13px] text-white/65 leading-relaxed">{body}</p>
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
      <div className="flex items-baseline gap-2.5 mb-1.5">
        <span className="text-2xl font-bold text-[#FF5C00] leading-none">{n}</span>
        <h3 className="text-sm font-bold">{title}</h3>
      </div>
      <p className="text-[13px] text-white/65 leading-relaxed">{body}</p>
    </div>
  );
}

function Plain({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
      <h3 className="text-sm font-bold mb-1.5">{title}</h3>
      <p className="text-[13px] text-white/65 leading-relaxed">{body}</p>
    </div>
  );
}
